import { useMemo } from "react"
import { usePlan } from "@/shared/hooks/usePlan"

interface UseAiImageQuotaResult {
  used: number
  remaining: number
  limit: number
  isExhausted: boolean
  isLoading: boolean
}

/**
 * Returns real-time credit balance for the current user.
 * Reads directly from the user doc (via usePlan / AuthContext) so it stays in
 * sync whenever creditsRemaining changes after a purchase or generation.
 *
 * @param _userId — kept for call-site API compatibility; value is unused because
 *                  credits come from the already-loaded userDetails in AuthContext.
 */
const useAiImageQuota = (_userId: string | null): UseAiImageQuotaResult => {
  const { creditsRemaining, creditsUsed, isLoading } = usePlan()

  return useMemo(
    () => ({
      used: creditsUsed,
      remaining: creditsRemaining,
      limit: creditsRemaining + creditsUsed,
      isExhausted: creditsRemaining <= 0,
      isLoading
    }),
    [creditsRemaining, creditsUsed, isLoading]
  )
}

export { useAiImageQuota }
